// Instala DevTools en la pagina: captura de errores, API `window.__heskala`
// (para cualquier agente con navegador) y el puente de Vite en desarrollo
// (para agentes con terminal, archivos o HTTP). Ver AGENTS.md, "DevTools".

import { devtools } from "./registry";
import { COMMAND_NAMES, runCommand, type DevtoolsCommand, type PageControl } from "./commands";
import { devtoolsEnabled, isConvexBlocked, setConvexBlocked } from "./convex-socket";
import { offlineSnapshot } from "./offline";

let forcedHidden = false;

export const browserPage: PageControl = {
  visibility: () => document.visibilityState,
  online: () => navigator.onLine,
  route: () => location.pathname + location.search,
  setHidden(hidden) {
    if (hidden === forcedHidden) return;
    forcedHidden = hidden;
    if (hidden) Object.defineProperty(document, "visibilityState", { configurable: true, get: () => "hidden" });
    else delete (document as unknown as Record<string, unknown>).visibilityState;
    document.dispatchEvent(new Event("visibilitychange"));
  },
  navigate(path) {
    history.pushState({}, "", path);
    dispatchEvent(new PopStateEvent("popstate"));
  },
  reload: () => location.reload(),
  setOffline: (offline) => setConvexBlocked(offline),
  isOffline: () => isConvexBlocked(),
  offline: () => offlineSnapshot(),
};

const call = async (command: string, args?: Record<string, unknown>) => {
  const res = await runCommand({ command, args }, browserPage);
  if (!res.ok) throw new Error(res.error);
  return res.result;
};

const page = () => ({ visibility: browserPage.visibility(), online: browserPage.online(), route: browserPage.route() });

/** API publica para agentes con navegador (Playwright, Chrome, etc.). */
// Lecturas sincronas (mas comodas en una consola o un `evaluate`); las que
// esperan algo (offline, comandos) devuelven una promesa.
export const heskalaApi = {
  schemaVersion: 1,
  commands: COMMAND_NAMES,
  snapshot: () => devtools.snapshot(page()),
  report: () => devtools.report(page()),
  errors: () => devtools.errors(),
  live: () => devtools.live(),
  offline: () => offlineSnapshot(),
  convex: {
    top: (n = 10) => devtools.top(n),
    reset: () => devtools.reset(),
  },
  simulate: {
    hidden: (hidden = true) => call("simulateHidden", { hidden }),
    offline: (offline = true) => call("simulateOffline", { offline }),
  },
  navigate: (path: string) => call("navigate", { path }),
  run: (cmd: DevtoolsCommand) => runCommand(cmd, browserPage),
};

declare global {
  interface Window {
    __heskala?: typeof heskalaApi;
  }
}

function captureErrors() {
  window.addEventListener("error", (e) => {
    devtools.addError({ source: "window", message: e.error?.stack ?? e.message ?? "Error" });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason;
    devtools.addError({ source: "promise", message: r instanceof Error ? (r.stack ?? r.message) : String(r) });
  });
  const original = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    original(...args);
    try {
      const text = args
        .map((a) => (a instanceof Error ? (a.stack ?? a.message) : typeof a === "string" ? a : safeJson(a)))
        .join(" ");
      devtools.addError({ source: "console", message: text });
    } catch {
      // nunca romper console.error
    }
  };
}

function safeJson(value: unknown) {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

let tabIdCache: string | null = null;

/** Id de la pestaña, estable entre recargas (sessionStorage). */
function devtoolsTabId() {
  if (tabIdCache) return tabIdCache;
  try {
    const saved = sessionStorage.getItem("heskala_devtools_tab");
    if (saved) return (tabIdCache = saved);
    const id = Math.random().toString(36).slice(2, 10);
    sessionStorage.setItem("heskala_devtools_tab", id);
    return (tabIdCache = id);
  } catch {
    return (tabIdCache = Math.random().toString(36).slice(2, 10));
  }
}

const currentSnapshot = () =>
  devtools.snapshot({ visibility: browserPage.visibility(), online: browserPage.online(), route: browserPage.route() });

/**
 * Daemon del devtool de escritorio (tools/devtool, docs/diseno-devtool-escritorio.md):
 * la pestaña reporta por WebSocket a `heskala-devd` en esta Mac, así los
 * agentes la ven aunque no corra Vite. En desarrollo siempre; en producción
 * solo con `localStorage["heskala:devd"] = "1"` (el navegador pediría permiso
 * de red local a cualquier usuario). Si el daemon no está, reintenta cada vez
 * más espaciado.
 */
function connectDaemon() {
  let optIn = false;
  try {
    optIn = localStorage.getItem("heskala:devd") === "1";
  } catch {
    // sin storage: solo en desarrollo
  }
  if (!import.meta.env.DEV && !optIn) return;
  const url = (import.meta.env.VITE_HESKALA_DEVD_URL as string | undefined) ?? "ws://127.0.0.1:4790/telemetry";
  const tabId = devtoolsTabId();
  let delay = 2_000;
  let socket: WebSocket | null = null;
  const send = (payload: unknown) => {
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(payload));
  };
  const push = () => send({ type: "snapshot", tabId, snapshot: currentSnapshot() });
  const open = () => {
    try {
      socket = new WebSocket(url);
    } catch {
      return;
    }
    socket.onopen = () => {
      delay = 2_000;
      push();
    };
    socket.onmessage = async (event) => {
      let msg: { type?: string; id?: string } & DevtoolsCommand;
      try {
        msg = JSON.parse(String(event.data));
      } catch {
        return;
      }
      if (msg.type !== "command") return;
      const result = await runCommand({ id: msg.id, command: msg.command, args: msg.args }, browserPage);
      send({ type: "result", tabId, ...result });
      push();
    };
    socket.onclose = () => {
      socket = null;
      setTimeout(open, delay);
      delay = Math.min(delay * 2, 300_000);
    };
  };
  open();
  setInterval(push, 5_000);
  document.addEventListener("visibilitychange", push);
}

/** Puente de Vite (solo `vite dev`): ver devtools/vite-plugin.ts. */
function connectViteBridge() {
  const hot = import.meta.hot;
  if (!hot) return;
  const tabId = devtoolsTabId();
  /**
   * En Vite, `import.meta.hot` existe apenas el modulo se evalua, pero
   * la conexion WS con el servidor HMR se establece despues (async). Si
   * llamamos `hot.send(...)` antes de que la conexion este abierta, Vite
   * lanza `SendBeforeConnectError`. Esto se ve cuando se monta DevTools
   * justo despues de un HMR update.
   *
   * La estrategia: diferir el primer push hasta que `vite:connect`
   * llegue (o ya este conectado, en cuyo caso disparamos al toque). El
   * interval y el listener de visibilitychange arrancan en cualquier
   * caso; si un push puntual falla por estar desconectado, lo silenciamos
   * (el siguiente tick lo va a reintentar). Asi no rompemos el ciclo de
   * actualizacion del panel Convex.
   */
  const safeSend = (event: string, payload: unknown) => {
    try {
      hot.send(event, payload as Record<string, unknown>);
    } catch (err) {
      // SendBeforeConnectError: la reconexion WS va a disparar el push.
      if (err instanceof Error && /SendBeforeConnectError/.test(err.message)) return;
      throw err;
    }
  };
  // Con el dev server apagado el cliente de Vite registra cada envío como
  // error en consola; mientras tanto el daemon (si está) sigue recibiendo.
  let viteDown = false;
  hot.on("vite:ws:disconnect", () => (viteDown = true));
  hot.on("vite:ws:connect", () => (viteDown = false));
  const push = () => {
    if (!viteDown) safeSend("heskala:devtools-snapshot", { tabId, snapshot: currentSnapshot() });
  };
  const onConnected = () => {
    push();
    hot.off("vite:connect", onConnected);
  };
  // Si ya esta conectado (reload normal), `vite:connect` se emite al
  // terminar el setup; si no, lo esperamos.
  hot.on("vite:connect", onConnected);
  // Fallback: intentamos el primer push ahora. Si Vite todavia no
  // conecto, lo silenciamos. En cuanto conecte, `vite:connect` dispara
  // el push otra vez. Asi cubrimos tanto el caso "ya conectado" (que
  // no emite `vite:connect` retroactivamente) como el caso "todavia
  // no".
  setTimeout(push, 0);
  setInterval(push, 5_000);
  document.addEventListener("visibilitychange", push);
  hot.on("heskala:devtools-command", async (cmd: DevtoolsCommand & { tabId?: string }) => {
    if (cmd.tabId && cmd.tabId !== tabId) return;
    const result = await runCommand(cmd, browserPage);
    safeSend("heskala:devtools-result", { tabId, ...result });
    push();
  });
}

let installed = false;

export function installDevtools() {
  if (installed || typeof window === "undefined" || !devtoolsEnabled()) return;
  installed = true;
  captureErrors();
  window.__heskala = heskalaApi;
  devtools.setSession({ version: typeof __GIT_VERSION__ === "string" ? __GIT_VERSION__ : undefined, convexUrl: import.meta.env.VITE_CONVEX_URL });
  connectViteBridge();
  connectDaemon();
}
